import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

export type ExportMedRow = {
  name: string;
  dosage?: string;
  quantity: number;
  unit?: string;
  expiryMonth: number;
  expiryYear: number;
  status: string | null;
  pricePerBox: number | null;
};

export type ExportBrandingOptions = {
  shareUrl?: string;
};

function escapeCsvCell(s: string) {
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function medicationsToCsv(rows: ExportMedRow[], filterLabel: string, options: ExportBrandingOptions = {}) {
  const header = ["Name", "Dosage", "Quantity", "Unit", "Expiry", "Status", "Price per box"];
  const lines = [header.join(",")];
  for (const m of rows) {
    const expiry = `${m.expiryMonth}/${m.expiryYear}`;
    const status = m.status || "active";
    const price = m.pricePerBox != null && Number.isFinite(m.pricePerBox) ? String(m.pricePerBox) : "";
    lines.push(
      [
        escapeCsvCell(m.name || ""),
        escapeCsvCell(m.dosage || ""),
        escapeCsvCell(String(m.quantity ?? "")),
        escapeCsvCell(m.unit || ""),
        escapeCsvCell(expiry),
        escapeCsvCell(status),
        escapeCsvCell(price),
      ].join(","),
    );
  }
  const bom = "\uFEFF";
  const shareLine = options.shareUrl
    ? `# Share risk reports: ${options.shareUrl}`
    : "# Share risk reports: https://expirydesk.co.za/signup?utm_source=report&utm_medium=csv&utm_campaign=share";
  return bom + `# ExpiryDesk export · ${filterLabel}\n${shareLine}\n${lines.join("\n")}\n`;
}

export async function medicationsToPdfBytes(rows: ExportMedRow[], filterLabel: string, options: ExportBrandingOptions = {}) {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);
  let page = doc.addPage([595.28, 841.89]);
  const { height } = page.getSize();
  const margin = 40;
  let y = height - margin;
  const lineH = 14;
  const titleSize = 12;

  function ensureSpace(linesNeeded: number) {
    if (y - linesNeeded * lineH < margin + 40) {
      page = doc.addPage([595.28, 841.89]);
      y = height - margin;
    }
  }

  page.drawText("ExpiryDesk — medication list", {
    x: margin,
    y,
    size: titleSize,
    font: fontBold,
    color: rgb(0.05, 0.2, 0.35),
  });
  y -= lineH * 1.5;
  page.drawText(`Filter: ${filterLabel}`, { x: margin, y, size: 10, font, color: rgb(0.25, 0.35, 0.45) });
  y -= lineH * 2;

  for (const m of rows) {
    ensureSpace(4);
    const line1 = `${m.name || "—"} ${m.dosage || ""}`.trim();
    const line2 = `Qty ${m.quantity ?? ""} ${m.unit || ""} · Exp ${m.expiryMonth}/${m.expiryYear} · ${m.status || "active"}`;
    const line3 =
      m.pricePerBox != null && Number.isFinite(m.pricePerBox)
        ? `Price/box ${m.pricePerBox}`
        : "";
    page.drawText(line1.slice(0, 120), { x: margin, y, size: 10, font: fontBold, color: rgb(0.1, 0.15, 0.2) });
    y -= lineH;
    page.drawText(line2.slice(0, 140), { x: margin, y, size: 9, font, color: rgb(0.2, 0.25, 0.35) });
    y -= lineH;
    if (line3) {
      page.drawText(line3, { x: margin, y, size: 9, font, color: rgb(0.35, 0.4, 0.45) });
      y -= lineH;
    }
    y -= 6;
  }

  const footerText = options.shareUrl
    ? `Generated with ExpiryDesk — ${options.shareUrl} · Share this report with suppliers/distributors.`
    : "Generated with ExpiryDesk — https://expirydesk.co.za/signup?utm_source=report&utm_medium=pdf&utm_campaign=share";
  const footerSize = 7.5;
  const footerColor = rgb(0.38, 0.42, 0.48);
  for (const p of doc.getPages()) {
    p.drawText(footerText, {
      x: margin,
      y: 22,
      size: footerSize,
      font,
      color: footerColor,
    });
  }

  const pdfBytes = await doc.save();
  return pdfBytes;
}
